/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Modal } from 'antd'
import {
  CloseOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import React, { useCallback, useEffect, useState } from 'react'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import req from '@sportninja/common/api/request'
import { App } from 'antd'
import { getErrorMessage } from '@sportninja/common/utils/utils'

interface BucketModalProps {
  isOpen: boolean
  onClose: () => void
  scheduleId: string
  registrationId: string
  refetch: (displayLoading?: boolean) => void
  bucketToEdit: any
}

const styles = css`
  .my-modal-body {
    max-height: calc(90vh - 110px);
    overflow-y: auto;
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
    position: sticky;
    bottom: 0;
    background: ${colors.SECONDARY_300};
    padding: 16px;
    border-top: 1px solid ${colors.SOFT_STEEL};
  }
  .my-modal-content {
    border: 1px solid #333;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    flex: 1,
    overflow: 'auto',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '90vh',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    // backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: colors.SECONDARY_300,
  },
}

const BucketModal: React.FC<BucketModalProps> = ({
  isOpen,
  onClose,
  scheduleId,
  registrationId,
  refetch,
  bucketToEdit,
}) => {
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState(bucketToEdit?.name || '')
  const [description, setDescription] = useState(
    bucketToEdit?.description || ''
  )
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState<Record<string, string>>({})

  useEffect(() => {
    if (bucketToEdit && isOpen) {
      setName(bucketToEdit?.name || '')
      setDescription(bucketToEdit?.description || '')
    }
  }, [bucketToEdit, isOpen])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const handleCancel = useCallback(() => {
    setName('')
    setDescription('')
    setFieldErrors([])
    setCustomError({})
    onClose()
  }, [onClose])

  const validateForm = useCallback(() => {
    const errors: string[] = []
    const customErrors: Record<string, string> = {}

    setFieldErrors([])
    setCustomError({})

    if (!name.trim()) {
      errors.push('name')
      customErrors.name = 'Name is required'
    }

    if (!scheduleId) {
      errors.push('scheduleId')
      customErrors.scheduleId = 'Schedule ID is required'
      message.error('Invalid schedule ID')
      return false
    }

    setFieldErrors(errors)
    setCustomError(customErrors)

    return errors.length === 0
  }, [name, description, scheduleId, message])

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      if (bucketToEdit?.id) {
        await req(
          `/registration/${scheduleId}/admin/bucket/${bucketToEdit?.id}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              bucket_name: name,
              bucket_description: description || '-',
            }),
          }
        )
      } else {
        await req(`/registration/${scheduleId}/admin/bucket`, {
          method: 'POST',
          body: JSON.stringify({
            bucket_name: name,
            bucket_description: description || '-',
            schedule_registration_id: registrationId,
          }),
        })
      }
      message.success(
        bucketToEdit?.uid
          ? `${bucketToEdit?.name} updated successfully`
          : `${name} created successfully`
      )
      refetch(false)
      handleCancel()
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      message.error(errorMessage)
      console.error('Error creating bucket:', error)
    } finally {
      setLoading(false)
    }
  }, [
    validateForm,
    bucketToEdit?.uid,
    bucketToEdit?.name,
    bucketToEdit?.id,
    message,
    name,
    refetch,
    handleCancel,
    scheduleId,
    description,
    registrationId,
  ])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 50%;
          margin-left: auto;
        `}
      >
        <Button
          text='Cancel'
          onClick={handleCancel}
          variant='secondary'
          disabled={loading}
        />
        <Button
          text='Save'
          onClick={handleSubmit}
          variant='primary'
          disabled={loading}
        />
      </div>
    )
  }, [handleCancel, handleSubmit, loading])

  return (
    <Modal
      width={600}
      closeIcon={closeIcon()}
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {bucketToEdit?.uid ? `Edit ${bucketToEdit?.name}` : 'New Bucket'}
        </Text>
      }
      open={isOpen}
      onCancel={handleCancel}
      footer={getFooter()}
      centered
      classNames={classNames}
      styles={modalStyles}
      maskClosable={false}
      css={css`
        .ant-modal-content {
          overflow: hidden;
          max-height: 95vh;
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          border-bottom: none !important;
        }
        .ant-modal-footer {
          position: sticky;
          bottom: 0;
          background: ${colors.SECONDARY_300};
          padding: 16px;
          border-top: 1px solid ${colors.SOFT_STEEL};
        }
      `}
    >
      <QuestionCircleOutlined
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          fontSize: 20,
          color: colors.SOLITUDE,
          position: 'absolute',
          top: 18,
          right: 48,
          zIndex: 1000,
          cursor: 'pointer',
        }}
        onClick={() => {
          window.open('https://support.sportninja.com/', '_blank')
        }}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 24px;
          padding: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <TextInput
            name='name'
            label='Name *'
            value={name}
            setValue={(value) => {
              setName(value)
            }}
            fieldErrors={fieldErrors}
            customError={customError}
            placeholder='Enter bucket name'
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <TextAreaInput
            name='description'
            label='Description'
            value={description}
            setValue={(value) => {
              setDescription(value)
              setFieldErrors([])
              setCustomError({})
            }}
            fieldErrors={fieldErrors}
            customError={customError}
            placeholder='Enter bucket description'
            rows={4}
          />
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: flex-end;
            margin-top: -8px;
          `}
        >
          <InfoCircleOutlined
            css={css`
              color: ${colors.PRIMARY};
              font-size: 16px;
            `}
          />
          <Text
            variant='paragraphSmall'
            color={colors.PRIMARY}
            weight='regular'
          >
            Learn more about buckets{' '}
            <a
              href='https://support.sportninja.com/'
              target='_blank'
              rel='noreferrer'
              css={css`
                color: ${colors.PRIMARY};
                text-decoration: underline;
              `}
            >
              here
            </a>
          </Text>
        </div>
      </div>
    </Modal>
  )
}

export default BucketModal
